/**
 * Composite wind field.
 *
 *   u = steering                                   (stormTranslation.ts)
 *     + V(r) f(y) e_theta                          (hollandProfile.ts, primary circulation)
 *     + u_r(r, y) e_r + w(r, y) e_y                (inflowUpdraft.ts, secondary circulation)
 *     + G(t) * curl(e psi)                         (curlNoise.ts, gated by the OU gust factor)
 *
 * with r measured from the moving storm center. Every term is
 * divergence-free on its own, so the sum is too (divergence.test.ts).
 *
 * Direction: with x east, z south, y up, counterclockwise from above is
 * positive rotation about +y. For offset (dx, dz) from the storm center the
 * CCW unit tangent is (dz, -dx) / r. At a point due east (dx > 0, dz = 0)
 * that gives -z, which is north. The Southern Hemisphere flips the sign.
 *
 * Calling createWindField(holland) with no options gives the pure Holland
 * field, constant with height, centered at the origin.
 */
import {
  coriolisParameter,
  hollandSpeedWithF,
  rotationSign,
  type HollandParams,
} from './hollandProfile';
import { createCurlNoise, type CurlNoise, type CurlNoiseParams } from './curlNoise';
import { createInflowUpdraft, type InflowUpdraft, type InflowUpdraftParams } from './inflowUpdraft';
import { createTranslationParams, type StormTranslationParams } from './stormTranslation';

export interface WindSample {
  x: number;
  y: number;
  z: number;
}

export interface WindFieldOptions {
  translation?: StormTranslationParams;
  inflow?: InflowUpdraftParams;
  noise?: CurlNoiseParams;
  /**
   * Tangential wind is full strength up to this height, then falls
   * linearly to zero at inflow.topM. Assumption, not fitted. Ignored
   * without `inflow`.
   */
  tangentialFullHeightM?: number;
}

export interface WindField {
  sample(xM: number, yM: number, zM: number, tS: number, out: WindSample): WindSample;
  /** The field without turbulence. Used by the debug overlay and tests. */
  sampleMean(xM: number, yM: number, zM: number, tS: number, out: WindSample): WindSample;
  /** Sets the storm center at t = 0. */
  setStormCenter(xM: number, zM: number): void;
  /** Storm center at time tS, written to out.x and out.z. */
  stormCenter(tS: number, out: WindSample): WindSample;
  /** OU gust factor multiplying turbulence. Values below 0 count as 0. */
  setGust(g: number): void;
  /** Current gust factor (after clamping). */
  readonly gust: number;
  /** Tangential full-strength height, m (Infinity without inflow). */
  readonly tangentialFullHeightM: number;
  /** |f| and rotation sign as of the last refreshParams(). */
  readonly coriolis: { readonly fAbs: number; readonly sign: 1 | -1 };
  /** Call after mutating holland, inflow, or noise params. */
  refreshParams(): void;
  readonly holland: HollandParams;
  readonly translation: StormTranslationParams;
  readonly inflow: InflowUpdraft | null;
  readonly noise: CurlNoise | null;
  /** Runtime toggles. Both default to true when the component exists. */
  readonly enabled: { inflow: boolean; turbulence: boolean };
}

export function createWindField(holland: HollandParams, opts: WindFieldOptions = {}): WindField {
  const translation = opts.translation ?? createTranslationParams();
  const inflow = opts.inflow ? createInflowUpdraft(opts.inflow) : null;
  const noise = opts.noise ? createCurlNoise(opts.noise) : null;
  const fullHeightM = opts.tangentialFullHeightM ?? Number.POSITIVE_INFINITY;
  const enabled = { inflow: inflow !== null, turbulence: noise !== null };

  let fAbs = 0;
  let sign: 1 | -1 = 1;
  let gust = 1;
  const sec: WindSample = { x: 0, y: 0, z: 0 };

  function refreshParams(): void {
    fAbs = Math.abs(coriolisParameter(holland.latitudeDeg));
    sign = rotationSign(holland.latitudeDeg);
    if (inflow) {
      inflow.params.rMaxM = holland.rMaxM;
      inflow.refresh();
    }
    if (noise) {
      noise.params.rPeakM = holland.rMaxM;
      noise.refresh();
    }
  }
  refreshParams();

  /** Tangential height factor: 1 below fullHeight, linear to 0 at the lid. */
  function tangentialHeightFactor(yM: number): number {
    if (!inflow || yM <= fullHeightM) return 1;
    const top = inflow.params.topM;
    if (yM >= top) return 0;
    return (top - yM) / (top - fullHeightM);
  }

  function compose(
    xM: number,
    yM: number,
    zM: number,
    tS: number,
    withTurbulence: boolean,
    out: WindSample,
  ): WindSample {
    const cx = translation.originXM + translation.steerXMps * tS;
    const cz = translation.originZM + translation.steerZMps * tS;
    const dx = xM - cx;
    const dz = zM - cz;
    const r = Math.sqrt(dx * dx + dz * dz);

    out.x = translation.steerXMps;
    out.y = 0;
    out.z = translation.steerZMps;

    if (r > 0) {
      const speed = hollandSpeedWithF(r, holland, fAbs) * tangentialHeightFactor(yM);
      if (speed > 0) {
        const k = (sign * speed) / r;
        out.x += k * dz;
        out.z -= k * dx;
      }
      if (inflow && enabled.inflow) {
        inflow.sample(r, yM, sec);
        const ur = sec.x / r;
        out.x += ur * dx;
        out.y += sec.y;
        out.z += ur * dz;
      }
    }
    if (withTurbulence && noise && enabled.turbulence && gust > 0) {
      noise.addTo(dx, yM, dz, tS, gust, out);
    }
    return out;
  }

  const coriolis = {
    get fAbs(): number {
      return fAbs;
    },
    get sign(): 1 | -1 {
      return sign;
    },
  };

  return {
    holland,
    translation,
    inflow,
    noise,
    enabled,
    coriolis,
    tangentialFullHeightM: fullHeightM,
    get gust(): number {
      return gust;
    },
    refreshParams,
    setStormCenter(xM: number, zM: number): void {
      translation.originXM = xM;
      translation.originZM = zM;
    },
    stormCenter(tS: number, out: WindSample): WindSample {
      out.x = translation.originXM + translation.steerXMps * tS;
      out.y = 0;
      out.z = translation.originZM + translation.steerZMps * tS;
      return out;
    },
    setGust(g: number): void {
      gust = g > 0 ? g : 0;
    },
    sample(xM, yM, zM, tS, out): WindSample {
      return compose(xM, yM, zM, tS, true, out);
    },
    sampleMean(xM, yM, zM, tS, out): WindSample {
      return compose(xM, yM, zM, tS, false, out);
    },
  };
}
